import * as React from "react";
import {
    Box,
    Container,
    Header,
    Button,
    FormField,
    Input,
    SpaceBetween,
    Flashbar,
    Modal,
    Spinner
} from "@amzn/awsui-components-react";
import { useAPI2 } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { AppError } from "@amzn/ask-legal-domain";
import { SPOOF_STORAGE_KEY, getStoredSpoof } from "../../api/common";

const SYSTEM_ADMIN_GROUP = "system-admin";

export const DevOpsHome = () => {
    const context = React.useContext(AppContext);
    const [viewType, setViewType] = React.useState<"Authorized" | "Unauthorized" | "Error" | null>(null);

    React.useEffect(() => {
        context.getIdentityWithCustomFields()
            .then((identity) => {
                const membership = identity.groupMembership || "";
                const isAdmin = membership.includes(SYSTEM_ADMIN_GROUP);
                setViewType(isAdmin ? "Authorized" : "Unauthorized");
            })
            .catch((e) => {
                console.error("Failed to check permissions:", e);
                setViewType("Error");
            });
    }, []);

    if (!viewType) {
        return (
            <Box textAlign="center" className="awsui-util-status-inactive">
                <Spinner size="big" /> Checking permissions
            </Box>
        );
    }

    if (viewType === "Error") {
        return <ErrorFlashbar error={AppError.create("An unexpected error occurred, please reload page", 500)} />;
    }

    if (viewType === "Unauthorized") {
        return (
            <Box textAlign="center" padding="xxl">
                <SpaceBetween size="m">
                    <Header variant="h1">Access Denied</Header>
                    <Box>You do not have permission to access DevOps tools. This page is restricted to system administrators.</Box>
                </SpaceBetween>
            </Box>
        );
    }

    return <DevOpsContent />;
};

const DevOpsContent = () => {
    const context = React.useContext(AppContext);
    const [userId, setUserId] = React.useState("");
    const [showConfirm, setShowConfirm] = React.useState(false);
    const revokeRunner = useAPI2(
        context.getDevOpsAPI().revokeUserPermissions
    );

    const onSubmit = () => {
        if (userId.trim()) {
            setShowConfirm(true);
        }
    };

    const onConfirm = () => {
        setShowConfirm(false);
        revokeRunner.invoke(userId.trim());
    };

    React.useEffect(() => {
        if (revokeRunner.status === "Succeeded") {
            setUserId("");
        }
    }, [revokeRunner.status]);

    const flashItems = React.useMemo(() => {
        if (revokeRunner.status === "Succeeded") {
            return [{
                id: "revoke-success",
                type: "success" as const,
                content: `Revocation request submitted for user ${revokeRunner.input}. The operation will complete shortly.`,
                dismissible: true,
                onDismiss: () => revokeRunner.resetStatus()
            }];
        }
        if (revokeRunner.status === "Error") {
            return [{
                id: "revoke-error",
                type: "error" as const,
                content: `Failed to revoke permissions: ${revokeRunner.err?.message || "Unknown error"}`,
                dismissible: true,
                onDismiss: () => revokeRunner.resetStatus()
            }];
        }
        return [];
    }, [revokeRunner.status, revokeRunner.input, revokeRunner.err]);

    return (
        <SpaceBetween size="l">
            <Header variant="h1" description="Restricted to altar-dev team members">
                DevOps Tools
            </Header>
            {flashItems.length > 0 && <Flashbar items={flashItems} />}
            <Container header={
                <Header variant="h2" description="Remove a user from all permission lists (admin, approver, editor, viewer) across all instances">
                    Revoke User Permissions
                </Header>
            }>
                <SpaceBetween size="m">
                    <FormField label="User Login">
                        <Input
                            value={userId}
                            onChange={({ detail }) => setUserId(detail.value)}
                            placeholder="Enter user login"
                            disabled={revokeRunner.status === "Running"}
                        />
                    </FormField>
                    <Button
                        variant="primary"
                        onClick={onSubmit}
                        loading={revokeRunner.status === "Running"}
                        disabled={!userId.trim()}
                    >
                        Submit Revocation
                    </Button>
                </SpaceBetween>
            </Container>
            <Modal
                visible={showConfirm}
                onDismiss={() => setShowConfirm(false)}
                header="Confirm Revocation"
                footer={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={() => setShowConfirm(false)}>Cancel</Button>
                        <Button variant="primary" onClick={onConfirm}>Confirm</Button>
                    </SpaceBetween>
                }
            >
                Are you sure you want to revoke all permissions for user <strong>{userId.trim()}</strong>? This will remove them from all permission lists (admin, approver, editor, viewer) across all instances.
            </Modal>
            <SpoofIdentitySection />
        </SpaceBetween>
    );
};

const SpoofIdentitySection = () => {
    const [spoofAlias, setSpoofAlias] = React.useState("");
    const [stored, setStored] = React.useState(getStoredSpoof);

    const handleSpoof = () => {
        if (!spoofAlias.trim()) return;
        const data = {
            id: spoofAlias.trim(),
        };
        localStorage.setItem(SPOOF_STORAGE_KEY, JSON.stringify(data));
        window.location.reload();
    };

    const handleClear = () => {
        localStorage.removeItem(SPOOF_STORAGE_KEY);
        window.location.reload();
    };

    return (
        <Container header={
            <Header
                variant="h2"
                description="View the application as another user for permission testing."
                actions={stored ? <Button onClick={handleClear}>Clear Spoof</Button> : undefined}
            >
                Spoof Identity
            </Header>
        }>
            {stored ? (
                <Flashbar items={[{
                    type: "warning",
                    content: `Currently viewing as: ${stored.id}`,
                    id: "spoof-active"
                }]} />
            ) : (
                <SpaceBetween size="m">
                    <FormField label="User Alias">
                        <Input
                            value={spoofAlias}
                            onChange={({ detail }) => setSpoofAlias(detail.value)}
                            placeholder="Enter alias to spoof"
                        />
                    </FormField>
                    <Button variant="primary" onClick={handleSpoof} disabled={!spoofAlias.trim()}>
                        Activate Spoof
                    </Button>
                </SpaceBetween>
            )}
        </Container>
    );
};
